import * as React from "react"

import { CarouselV2, Icon, cva } from "@iyk/ui"
import { Option } from "effect"
import { ArrowKeyDirection, ArrowKeyNavigation } from "./ArrowKeyNavigation.ts"

const Root = ({ containerClassName, options, ...props }: MediaCarousel.Root) => {
  const containerRef = React.useRef<HTMLDivElement>(null)

  return (
    <div ref={containerRef} className={containerClassName}>
      <CarouselV2.Root
        options={{
          loop: false,
          watchDrag: (_, event) => {
            const container = containerRef.current
            if (!container) return true

            return isPointerOutsideVideoControls(container, event)
          },
          ...options
        }}
        {...props}
      />
    </div>
  )
}

const PreviousButton = () => {
  const carousel = CarouselV2.useCarousel()

  return (
    <button
      type="button"
      className={classNameForButton({ isHidden: !carousel.canGoToPreviousSlide(), className: "left-2" })}
      onClick={carousel.goToPreviousSlide}
    >
      <Icon.ChevronLeft />
    </button>
  )
}

const NextButton = () => {
  const carousel = CarouselV2.useCarousel()

  return (
    <button
      type="button"
      className={classNameForButton({ isHidden: !carousel.canGoToNextSlide(), className: "right-2" })}
      onClick={carousel.goToNextSlide}
    >
      <Icon.ChevronRight className="size-4" />
    </button>
  )
}

const useArrowKeyNavigation = (scope: React.RefObject<HTMLElement | null>) => {
  const { goToPreviousSlide, goToNextSlide, selectedScrollSnap, scrollSnapList } = CarouselV2.useCarousel()
  // before Embla is ready it reports slide 0, whichever slide the carousel opens on
  const selectedSlide = scrollSnapList().length > 0 ? selectedScrollSnap() : undefined

  React.useEffect(() => {
    const element = scope.current
    if (!element) return

    // capture phase on the scope: arrows reach the carousel before anything inside it can swallow them
    const onKeyDown = (event: KeyboardEvent) => {
      const direction = ArrowKeyNavigation.directionOf(event)
      if (Option.isNone(direction)) return
      event.stopPropagation()
      ArrowKeyDirection.$match(direction.value, {
        Previous: () => {
          goToPreviousSlide()
        },
        Next: () => {
          goToNextSlide()
        }
      })
    }

    element.addEventListener("keydown", onKeyDown, { capture: true })
    return () => {
      element.removeEventListener("keydown", onKeyDown, { capture: true })
    }
  }, [scope, goToPreviousSlide, goToNextSlide])

  // focus goes to the scope, so it has to be focusable (a dialog popup is)
  React.useEffect(() => {
    const element = scope.current
    if (!element || selectedSlide === undefined) return
    if (ArrowKeyNavigation.isFocusLeftBehind(element, document.activeElement, selectedSlide)) element.focus()
  }, [scope, selectedSlide])
}

const classNameForButton = cva(
  [
    "hidden sm:flex sm:opacity-0 transition-all duration-300",
    "backdrop-blur-md bg-black/40 hover:bg-black/60 focus-visible:bg-black/60",
    "rounded-full size-8 items-center justify-center absolute top-1/2 -translate-y-1/2 z-10"
  ],
  {
    variants: {
      isHidden: {
        false: "sm:group-hover:opacity-100"
      }
    }
  }
)

const isPointerOutsideVideoControls = (container: HTMLDivElement, event: MouseEvent | TouchEvent) => {
  const videoControlsHeight = 40
  const containerHeight = container.getBoundingClientRect().height
  const containerTop = container.getBoundingClientRect().top
  const allowedArea = containerHeight - videoControlsHeight

  const clientY =
    "clientY" in event
      ? event.clientY
      : "touches" in event && event.touches.length > 0
        ? event.touches[0].clientY
        : 0
  const y = clientY - containerTop

  if (y <= allowedArea) {
    return true
  }

  return false
}

export namespace MediaCarousel {
  export type Root = CarouselV2.Root & {
    options?: Omit<CarouselV2.Root["options"], "watchDrag">
    containerClassName?: string
  }
}

export const MediaCarousel = {
  Root,
  PreviousButton,
  NextButton,

  Slide: CarouselV2.Slide,
  Slides: CarouselV2.Slides,
  useCarousel: CarouselV2.useCarousel,
  useArrowKeyNavigation,
  Indicators: CarouselV2.Indicators,
  LeftArrow: CarouselV2.LeftArrow,
  RightArrow: CarouselV2.RightArrow,
  Counter: CarouselV2.Counter
}
